/**
 * Single source of truth for marketing-page SEO.
 *
 * Used in three places so they can never drift:
 *  1. React pages — feed `useSEO` (title/description/keywords/canonical).
 *  2. `useJsonLd` — emit FAQPage structured data at runtime (SPA navigation).
 *  3. `scripts/seo/prerender.mjs` — bake the same <head> + FAQPage JSON-LD
 *     into static per-route HTML so non-JS crawlers and AI search engines
 *     (which often do not execute JavaScript) can read it on first byte.
 *
 * NOTE: Google requires FAQPage schema text to match the visible on-page
 * copy. The questions/answers here are intentionally identical (including the
 * lowercase brand styling) to what is rendered in the FAQ accordions.
 */

export interface FaqItem {
  question: string;
  answer: string;
}

export interface RouteSeo {
  /** Route path, also used as the canonical (appended to the base URL). */
  path: string;
  title: string;
  description: string;
  keywords?: string;
  /** Present on the five experience pages; drives the FAQPage schema. */
  faqs?: FaqItem[];
  /**
   * The route's LCP image (the modality hero, client-rendered by React and
   * therefore invisible to the static HTML's preload scanner). When set,
   * prerender.mjs bakes a responsive <link rel="preload" as="image"> into
   * the route's static HTML. Keep srcset/sizes in lockstep with the
   * md-hero <img> in site.jsx / photoSrcSet in data.js.
   */
  lcpImage?: { srcset: string; sizes: string };
}

/** sizes of the md-hero detail picture — mirrors site.jsx. */
const MD_HERO_SIZES = "(max-width: 760px) 93vw, 568px";

export const routes: RouteSeo[] = [
  {
    path: "/",
    title:
      "Recovery Club Century City, Cape Town | Self-Guided Recovery — ZUZU",
    description:
      "Self-guided recovery club in Century City, Cape Town. Nordic sauna, cold plunge, Normatec compression boots, red light therapy & InBody scans. Book from R100.",
    keywords:
      "recovery club Century City, recovery club Cape Town, sauna cold plunge Cape Town, contrast therapy Century City, nordic sauna Century City, wellness club Cape Town, post workout recovery Cape Town, self guided recovery Cape Town",
  },
  {
    path: "/about",
    title: "About ZUZU Recovery Club | Century City, Cape Town",
    description:
      "ZUZU is a self-guided recovery club in Century City, Cape Town — nordic sauna, cold plunge, Normatec compression boots, red light therapy & InBody scans, chosen for evidence, not trends.",
    keywords:
      "about ZUZU recovery club, recovery club Cape Town story, wellness club Century City, self guided recovery Cape Town",
  },
  {
    path: "/equipment",
    title:
      "Recovery Rooms Century City | Sauna, Plunge, Compression & Red Light — ZUZU",
    description:
      "Explore ZUZU's five recovery rooms in Century City, Cape Town: nordic sauna, cold plunge, Normatec compression boots, red light therapy and an InBody scan. Book any room from R100.",
    keywords:
      "recovery rooms Cape Town, sauna cold plunge compression Century City, wellness rooms Cape Town, InBody scan Cape Town",
  },
  {
    path: "/contact",
    title: "Contact & Visit ZUZU | Century City, Cape Town Recovery Club",
    description:
      "Visit ZUZU at 5 Edison Way, Century City, Cape Town. Opening hours, directions and contact details for bookings, memberships and group sessions.",
    keywords:
      "ZUZU contact, ZUZU Century City address, recovery club Cape Town hours, visit ZUZU Cape Town",
  },
  {
    path: "/legal",
    title: "Terms, Privacy & Cookies | ZUZU Recovery Club",
    description:
      "ZUZU's terms of use, house rules, POPIA privacy policy and cookie information for members of the Century City, Cape Town recovery club.",
    keywords: "ZUZU terms, ZUZU privacy policy, ZUZU house rules",
  },
  {
    path: "/signup/membership",
    title:
      "Off-Peak Membership Cape Town | Unlimited Recovery from R899/mo — ZUZU",
    description:
      "Unlimited off-peak access to ZUZU's recovery circuit in Century City, Cape Town — sauna, cold plunge, compression boots, red light & InBody. From R899/month, with no automatic renewal.",
    keywords:
      "recovery club membership Cape Town, unlimited sauna membership Cape Town, ZUZU membership pricing, wellness membership Century City",
  },
  {
    path: "/signup/credits",
    title: "Recovery Credits Cape Town | Pay-As-You-Go from R100 — ZUZU",
    description:
      "Buy ZUZU recovery credits — R100 each, no contract, valid three years. Use across the sauna, cold plunge, compression boots, red light and InBody scan in Century City, Cape Town.",
    keywords:
      "recovery credits Cape Town, pay as you go wellness Cape Town, drop in sauna Cape Town, gift credits ZUZU",
  },
  {
    path: "/signup/private",
    title: "Book the Private Room Cape Town | Group Recovery Sessions — ZUZU",
    description:
      "Reserve the whole ZUZU recovery club in Century City for your group — up to 10 people, from R600. Ideal for birthdays, bachelorette parties, teams and corporate off-sites.",
    keywords:
      "private room booking Cape Town, group recovery booking Century City, book whole venue Cape Town, private sauna hire Cape Town",
  },
  {
    path: "/book",
    title:
      "Book a Recovery Session Cape Town | Live Availability — Century City",
    description:
      "See live availability at ZUZU Recovery Club in Century City and book your slot — sauna, cold plunge, compression boots, red light therapy, InBody scan, or the whole private room for your group.",
    keywords:
      "book sauna Cape Town, book cold plunge Century City, recovery booking Cape Town, sauna availability Century City, book private recovery room Cape Town",
  },
  {
    path: "/pricing",
    title:
      "Recovery Club Pricing Cape Town | Credits & Memberships — Century City",
    description:
      "Flexible recovery pricing in Century City, Cape Town. Pay-as-you-go from R100, or unlimited off-peak membership from R899/month. Sauna, cold plunge, compression boots, InBody scan & red light therapy.",
    keywords:
      "recovery club pricing Century City, recovery club pricing Cape Town, sauna membership Cape Town, wellness club membership Cape Town, recovery credits Cape Town, unlimited recovery Cape Town",
  },
  {
    path: "/experience/plunge",
    lcpImage: {
      srcset:
        "/redesign/assets/imagery/cp-dunk-bath-cut-720.421245ab.webp 720w, /redesign/assets/imagery/cp-dunk-bath-cut.bcee0a75.webp 1462w",
      sizes: MD_HERO_SIZES,
    },
    title: "Cold Plunge Century City, Cape Town | Ice Bath Sessions — ZUZU",
    description:
      "Premium cold plunge tubs in Century City, Cape Town. Two tubs at 7°C & 12°C to reduce inflammation, manage stress & speed recovery. Self-guided sessions from R100.",
    keywords:
      "cold plunge Century City, cold plunge Cape Town, ice bath Cape Town, cold plunge therapy Cape Town, cold water immersion Cape Town, contrast therapy Century City, ice bath Century City",
    faqs: [
      {
        question: "what temperature is the cold plunge?",
        answer:
          "we have two dunk cold plunge tubs - 7°c and 12°c - so you can pick the cold plunge or a gentler start and build up from there.",
      },
      {
        question: "how long should I stay in?",
        answer:
          "start with 1-3 minutes and gradually build up. most people find 3-5 minutes provides significant benefits. never exceed 10-15 minutes.",
      },
      {
        question: "is it safe for everyone?",
        answer:
          "cold plunging is generally safe, but consult your doctor if you have heart conditions, high blood pressure, or are pregnant. avoid if you're ill or have open wounds.",
      },
      {
        question: "should I use sauna before or after?",
        answer:
          "both work! sauna before plunge is great for contrast therapy. plunge before sauna works well for a warm finish. experiment to find what feels best for you.",
      },
      {
        question: "how is the water kept clean?",
        answer:
          "our dunk cold plunge features a built-in ozonator and filtration system that continuously purifies the water, ensuring a hygienic experience for every user.",
      },
    ],
  },
  {
    path: "/experience/sauna",
    lcpImage: {
      srcset:
        "/redesign/assets/imagery/sauna-interior-v2-tall-480.324b22dc.webp 480w, /redesign/assets/imagery/sauna-interior-v2-tall.89733994.webp 710w",
      sizes: MD_HERO_SIZES,
    },
    title:
      "Nordic Sauna Century City, Cape Town — 90°C Sauna | ZUZU Recovery Club",
    description:
      "A true 90°C nordic sauna in Century City, Cape Town. Private cabin for up to eight, towels provided, book by the session. Pair it with the cold plunge next door.",
    keywords:
      "nordic sauna Century City, nordic sauna Cape Town, sauna for muscle recovery Cape Town, sauna Cape Town, private sauna Century City, sauna booking Cape Town",
    faqs: [
      {
        question: "how hot does it actually get?",
        answer:
          "90°c — a proper nordic heat. dry and intense, so most members do ten to fifteen minutes a round and step out to cool down before going again.",
      },
      {
        question: "how long should my session be?",
        answer:
          "we recommend starting with 20-30 minute sessions and gradually increasing to 45 minutes as your body adapts. listen to your body and exit if you feel uncomfortable.",
      },
      {
        question: "what should I wear?",
        answer:
          "wear comfortable, loose clothing or swimwear. towels are in the bathroom, so grab one to sit on and one to wipe sweat, then leave them in the laundry bin there when you go. avoid heavy clothing that may restrict heat absorption.",
      },
      {
        question: "how many can use the sauna at once?",
        answer:
          "one nordic cabin — up to eight people at a time. the cabin is private to your booking, so it's just you or the group you came with.",
      },
      {
        question: "how often should I use the sauna?",
        answer:
          "for general wellness, 2-4 sessions per week is ideal. athletes may benefit from daily sessions for recovery. always stay hydrated and listen to your body.",
      },
    ],
  },
  {
    path: "/experience/compress",
    lcpImage: {
      srcset:
        "/redesign/assets/imagery/compression-boots-flatlay-720.2341d02f.webp 720w, /redesign/assets/imagery/compression-boots-flatlay.11d7f816.webp 1084w",
      sizes: MD_HERO_SIZES,
    },
    title:
      "Normatec Compression Boots Century City, Cape Town | Leg Recovery — ZUZU",
    description:
      "Normatec Premier compression boots in Century City, Cape Town. Sequential pulse therapy to flush lactic acid, reduce soreness & speed recovery. Book from R100.",
    keywords:
      "compression boots Century City, compression boots Cape Town, Normatec compression therapy Cape Town, leg compression recovery Cape Town, Normatec boots Cape Town, post workout leg recovery Cape Town",
    faqs: [
      {
        question: "how does compression therapy work?",
        answer:
          "the boots use sequential pulse technology to inflate and deflate in waves, mimicking the muscle pump of the legs. this helps push blood and lymphatic fluid, reducing swelling and speeding recovery.",
      },
      {
        question: "how long should a session be?",
        answer:
          "typical sessions are 15-30 minutes. you can use them longer, but 20-30 minutes is usually sufficient for noticeable benefits.",
      },
      {
        question: "can I use them every day?",
        answer:
          "yes! compression therapy is gentle and can be used daily. many athletes use them before and after training for both preparation and recovery.",
      },
      {
        question: "what should I wear?",
        answer:
          "wear comfortable pants or leggings. the boots fit over most clothing, but avoid bulky jeans or thick materials that might restrict the compression.",
      },
      {
        question: "is it good for non-athletes?",
        answer:
          "absolutely! compression therapy benefits anyone who spends long hours on their feet, travels frequently, or simply wants to improve circulation and reduce leg fatigue.",
      },
    ],
  },
  {
    path: "/experience/scan",
    lcpImage: {
      srcset:
        "/redesign/assets/imagery/inbody-120-catalogue-720.03c47bec.webp 720w, /redesign/assets/imagery/inbody-120-catalogue-1152.63c0deaf.webp 1152w, /redesign/assets/imagery/inbody-120-catalogue.a69dba24.webp 1920w",
      sizes: MD_HERO_SIZES,
    },
    title:
      "InBody Scan Century City, Cape Town | Body Composition Analysis — ZUZU",
    description:
      "InBody 120 body composition scanner in Century City, Cape Town. 30-second analysis of muscle mass, body fat & hydration. Track progress over time. Book from R100.",
    keywords:
      "InBody scan Century City, InBody scan Cape Town, body composition test Cape Town, body fat analysis Cape Town, InBody 120 Cape Town, muscle mass measurement Cape Town, body composition scan Century City",
    faqs: [
      {
        question: "how accurate is the InBody scan?",
        answer:
          "InBody scans are highly accurate for tracking changes over time. while no method is 100% precise, InBody uses direct segmental measurement which is more accurate than many alternatives.",
      },
      {
        question: "how often should I scan?",
        answer:
          "we recommend scanning every 2-4 weeks. scanning too frequently can be misleading as your body composition doesn't change dramatically day to day.",
      },
      {
        question: "what affects scan accuracy?",
        answer:
          "hydration levels, recent meals, exercise, and time of day can affect results. for consistent tracking, try to scan under similar conditions each time.",
      },
      {
        question: "what do the results mean?",
        answer:
          "your results show muscle mass, body fat mass, body water, and more. the app provides explanations for each metric and how you compare to healthy ranges for your age and gender.",
      },
      {
        question: "can I use it if I have implants?",
        answer:
          "if you have a pacemaker or other electronic implant, consult your doctor before using InBody. the scan uses a small electrical current that may interfere with some devices.",
      },
    ],
  },
  {
    path: "/experience/redlight",
    lcpImage: {
      srcset:
        "/redesign/assets/imagery/red-light-on-dark-720.9756ce9e.webp 720w, /redesign/assets/imagery/red-light-on-dark.07ba482a.webp 1086w",
      sizes: MD_HERO_SIZES,
    },
    title:
      "Red Light Therapy Century City, Cape Town | Recovery Sessions — ZUZU",
    description:
      "Red light therapy in Century City, Cape Town. Skin health, muscle recovery & cellular energy. Non-invasive sessions from 10–20 minutes. Book from R100.",
    keywords:
      "red light therapy Century City, red light therapy Cape Town, near infrared therapy Cape Town, LED light therapy Century City, red light therapy muscle recovery Cape Town, photobiomodulation Cape Town",
    faqs: [
      {
        question: "is red light therapy safe?",
        answer:
          "yes, red light therapy is completely safe. unlike UV light, red and near-infrared wavelengths don't damage skin. there's no risk of burns or skin cancer.",
      },
      {
        question: "what does it feel like?",
        answer:
          "you'll feel a gentle warmth from the light. it's relaxing and many people find it meditative. there's no discomfort or pain.",
      },
      {
        question: "how soon will I see results?",
        answer:
          "some benefits like improved mood or reduced muscle soreness can be felt immediately. skin improvements typically take 4-8 weeks of consistent use.",
      },
      {
        question: "can I use it with other treatments?",
        answer:
          "absolutely! red light therapy pairs well with sauna and cold plunge. using it after sauna can enhance skin benefits, and it can help with recovery after cold exposure.",
      },
      {
        question: "should I wear eye protection?",
        answer:
          "while red light is safe for eyes, some people prefer to wear eye protection or simply close their eyes during treatment, especially when treating the face.",
      },
    ],
  },
  {
    path: "/corporate-team-building",
    title:
      "Corporate Team Building Cape Town | Wellness Off-Sites — ZUZU Century City",
    description:
      "Corporate team building in Cape Town done differently. Reserve the whole ZUZU recovery club in Century City for your team — nordic sauna, cold plunge, compression boots & red light. Up to 10 people, from R600.",
    keywords:
      "corporate team building Cape Town, team building Cape Town, corporate team building Century City, corporate wellness Cape Town, team building activities Cape Town, company off-site Cape Town, team wellness day Cape Town, group recovery Cape Town, corporate events Century City, team building venue Cape Town",
    faqs: [
      {
        question: "where in cape town are you based?",
        answer:
          "we're at 5 edison way, century city, cape town — easy to reach from the cbd, century city offices and the northern suburbs, with parking on site. it's a central spot for teams travelling in from across cape town.",
      },
      {
        question: "how many people can you host for a team building session?",
        answer:
          "the private room is built for groups of up to 10 people. for larger teams we can split you across back-to-back sessions so everyone gets the full circuit — get in touch and we'll help you plan it.",
      },
      {
        question: "what does a corporate team building session include?",
        answer:
          "your team gets the whole space to yourselves: the 90°c nordic sauna, both cold plunge tubs (7°c & 12°c), normatec compression boots, red-light therapy and an inbody body-composition scan. you can play your own music through the room and move through the circuit together.",
      },
      {
        question: "why is recovery good for team building?",
        answer:
          "contrast therapy — heat, cold and breath — is a shared challenge that gets a team out of the boardroom and doing something real together. it's low-pressure, screen-free and genuinely good for everyone's stress, sleep and recovery, so people leave feeling better rather than drained.",
      },
      {
        question: "how much does a corporate session cost?",
        answer:
          "private room hire is R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak and 6am–9am/4pm–8pm peak; saturday and sunday have different off-peak hours (sunday is off-peak all day), and a booking spanning both windows is charged pro rata. for up to 10 people, you can book from 30 minutes (from R600 off-peak), so a session scales to your team's time and budget.",
      },
      {
        question:
          "can we book outside office hours, and what about cancellations?",
        answer:
          "yes — we're open seven days a week (mon–fri 6am–8pm, sat/sun 8:30am–5pm) and peak slots cover early mornings and after-work on weekdays, so you can run a session around the working day. cancelling is free more than 48 hours out; between 48 and 12 hours you get half back, and inside 12 hours of the start nothing comes back. refunds are paid as credits onto your account rather than back to your card.",
      },
    ],
  },
  {
    path: "/birthday-party",
    title:
      "Birthday Party Cape Town | Private Recovery Celebration — ZUZU Century City",
    description:
      "Celebrate your birthday differently in Cape Town. Book out the whole ZUZU recovery club in Century City — nordic sauna, cold plunge & red light for up to 10. A feel-good, alcohol-free party from R600.",
    keywords:
      "birthday party Cape Town, private party venue Cape Town, sauna party Cape Town, alcohol free birthday Cape Town, unique birthday ideas Cape Town, sober party Cape Town, birthday venue Century City, wellness birthday Cape Town",
    faqs: [
      {
        question: "can I book the whole space for a birthday?",
        answer:
          "yes — the private room is yours for the booking, for groups of up to 10. you get all five circuits to your group alone, with no public sharing the space.",
      },
      {
        question: "is it suitable if my friends aren't into fitness?",
        answer:
          "completely. everything is self-guided, so each person goes as hard or as gentle as they like. the sauna, cold plunge and red light suit every fitness level — nobody needs to 'train' for it.",
      },
      {
        question: "can we bring food, drinks or a cake?",
        answer:
          "ZUZU is a recovery space rather than a function venue, so it isn't set up for catering or a bar. most groups pair their session with a meal nearby in century city. if you'd like to bring something small like a cake, get in touch first and we'll let you know what works.",
      },
      {
        question: "how much does a birthday session cost?",
        answer:
          "private room hire is R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak and 6am–9am/4pm–8pm peak; saturday and sunday have different off-peak hours (sunday is off-peak all day), and a booking spanning both windows is charged pro rata. for up to 10 people, you can book from 30 minutes — from R600 off-peak — so it scales to your group and budget.",
      },
      {
        question: "can guests get a gift card or come back?",
        answer:
          "yes — gift cards send recovery credits valid for three years, so they make easy party favours and let guests return whenever suits them.",
      },
      {
        question: "when's the best time to book a birthday?",
        answer:
          "we're open seven days a week. weekday off-peak (9am–4pm) is the calmest and best value; peak slots cover early mornings and evenings. saturday and sunday have different off-peak hours (sunday is off-peak all day). private bookings take the whole space, so reserve ahead for popular dates.",
      },
    ],
  },
  {
    path: "/bachelorette-party",
    title:
      "Bachelorette Party Cape Town | Private Recovery Celebration — ZUZU Century City",
    description:
      "Celebrate the bachelorette differently in Cape Town. Book out the whole ZUZU recovery club in Century City — nordic sauna, cold plunge, compression & red light for up to 10. From R600.",
    keywords:
      "bachelorette party Cape Town, hen party Cape Town, bachelorette venue Cape Town, sauna bachelorette Cape Town, alcohol free bachelorette Cape Town, bachelorette activities Cape Town, spa bachelorette Century City, bridal party Cape Town",
    faqs: [
      {
        question: "can I book the whole space for a bachelorette party?",
        answer:
          "yes — the private room is yours for the booking, for groups of up to 10. the whole bridal party gets all five circuits with no public sharing the space.",
      },
      {
        question: "is it suitable if not everyone is into fitness?",
        answer:
          "completely. everything is self-guided, so each guest goes as hard or as gentle as they like. the sauna, cold plunge and red light suit every fitness level — nobody needs to 'train' for it.",
      },
      {
        question: "can we bring décor, drinks or a cake?",
        answer:
          "ZUZU is a recovery space rather than a function venue, so it isn't set up for catering, décor or a bar. most groups pair their session with brunch or a meal nearby in century city. get in touch first if you'd like to bring something small.",
      },
      {
        question: "how much does a bachelorette session cost?",
        answer:
          "private room hire is R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak and 6am–9am/4pm–8pm peak; saturday and sunday have different off-peak hours (sunday is off-peak all day), and a booking spanning both windows is charged pro rata. for up to 10 people, you can book from 30 minutes — from R600 off-peak.",
      },
      {
        question: "can we gift the bride a session afterwards?",
        answer:
          "yes — gift cards send recovery credits valid for three years, so the bride, or any guest, can come back and use them whenever suits.",
      },
      {
        question: "when's the best time to book a bachelorette?",
        answer:
          "we're open seven days a week. weekday off-peak (9am–4pm) is calmest and best value; peak slots cover early mornings and evenings. saturday and sunday have different off-peak hours. private bookings take the whole space, so reserve ahead for popular dates.",
      },
    ],
  },
  {
    path: "/team-recovery",
    title:
      "Sports Team Recovery Cape Town | Athlete Contrast Therapy — ZUZU Century City",
    description:
      "Group recovery for sports teams, run clubs & training partners in Cape Town. Private contrast therapy — nordic sauna, cold plunge & compression boots for up to 10 athletes. Book the whole space from R600.",
    keywords:
      "sports team recovery Cape Town, athlete recovery Cape Town, team recovery session Cape Town, contrast therapy athletes Cape Town, run club recovery Cape Town, cold plunge for athletes Cape Town, muscle recovery Cape Town, group recovery Century City",
    faqs: [
      {
        question: "is contrast therapy actually good for recovery?",
        answer:
          "hot-cold contrast is a long-standing recovery tool — the heat boosts circulation and the cold helps manage inflammation and soreness. it won't replace good sleep and nutrition, but plenty of athletes use it to feel fresher between sessions.",
      },
      {
        question: "how many athletes can come at once?",
        answer:
          "the room takes up to 10 people per session. for a bigger squad we can plan back-to-back slots so everyone gets the full circuit — get in touch and we'll help you schedule it.",
      },
      {
        question: "should we come before or after training?",
        answer:
          "most teams book recovery after a hard session, a race or a match. a lighter contrast reset on a rest day works too. both are fine — it depends on what your training week needs.",
      },
      {
        question: "how long should a recovery session be?",
        answer:
          "30 to 60 minutes is typical — enough for a sauna-to-plunge round or two plus time in the compression boots. you can book from 30 minutes and extend if the group wants a longer reset.",
      },
      {
        question: "how much does a team session cost?",
        answer:
          "private room hire is R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak and 6am–9am/4pm–8pm peak; saturday and sunday have different off-peak hours (sunday is off-peak all day), and a booking spanning both windows is charged pro rata. for up to 10, from 30 minutes (from R600 off-peak), so it fits around training budgets.",
      },
      {
        question: "can we book a regular weekly recovery slot?",
        answer:
          "yes — get in touch and we'll help you set up a standing booking so recovery becomes part of the training week. off-peak slots are the best value for a regular ritual (weekday off-peak is 9am–4pm; saturday and sunday hours differ, with sunday off-peak all day).",
      },
    ],
  },
  {
    path: "/date-night",
    title:
      "Date Night Cape Town | Couples Sauna & Cold Plunge — ZUZU Century City",
    description:
      "A different date night in Cape Town. Share a private nordic sauna, cold plunge and red-light session for two in Century City — screen-free, alcohol-free, from R600. Perfect for anniversaries.",
    keywords:
      "date night Cape Town, couples spa Cape Town, romantic things to do Cape Town, couples sauna Cape Town, anniversary ideas Cape Town, things to do as a couple Cape Town, unique date Cape Town, couples wellness Century City",
    faqs: [
      {
        question: "can we book just for two?",
        answer:
          "yes. the whole room is private to you even when it's just the two of you, and you can book from 30 minutes — from R600 off-peak — so a date doesn't need a big group or a long booking.",
      },
      {
        question: "is it private and romantic enough for a date?",
        answer:
          "the entire space is yours for the booking — no public, no queue. it's quiet, screen-free and you play your own music, so it's an easy setting to actually be present with each other.",
      },
      {
        question: "what should we wear and bring?",
        answer:
          "swimwear is ideal for the sauna and cold plunge. towels are in the bathroom (please leave them in the laundry bin there when you go) — just bring water to stay hydrated between rounds.",
      },
      {
        question: "how much does a couples session cost?",
        answer:
          "room hire is the same whether it's two of you or ten: R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak, sat/sun hours differ (sunday is off-peak all day), and a booking spanning both windows is pro rata. you can book from 30 minutes, from R600 off-peak.",
      },
      {
        question: "can I surprise my partner with this as a gift?",
        answer:
          "yes — a gift card sends recovery credits valid for three years, so you can gift the session and let your partner pick the moment.",
      },
      {
        question: "is it good for valentine's day or an anniversary?",
        answer:
          "it's a great alternative to a packed restaurant. we're open seven days a week, but popular dates book up — reserve ahead so the slot you want is yours.",
      },
    ],
  },
  {
    path: "/gift-a-session",
    title: "Wellness Gift Cape Town | Recovery Gift Cards — ZUZU Century City",
    description:
      "Give the gift of recovery in Cape Town. ZUZU gift cards send credits for nordic sauna, cold plunge, red light & InBody scans in Century City. Credits are valid for three years — a feel-good gift for anyone.",
    keywords:
      "wellness gift Cape Town, experience gift Cape Town, gift card Cape Town, spa gift voucher Cape Town, recovery gift Cape Town, gift ideas Cape Town, wellness voucher Century City, gift for him her Cape Town",
    faqs: [
      {
        question: "do ZUZU gift cards expire?",
        answer:
          "no — gift cards send recovery credits valid for three years. there's no countdown and no pressure, so the person you're gifting can use them whenever life allows.",
      },
      {
        question: "what can the credits be used on?",
        answer:
          "anything in the circuit: the 90°c nordic sauna, cold plunges, normatec compression boots, red-light therapy or an inbody body-composition scan. one credit is 30 minutes off-peak (15 minutes peak) — off-peak/peak hours vary by day, and sunday is off-peak all day.",
      },
      {
        question: "how do I buy and send a gift?",
        answer:
          "get in touch and we'll help you set up a gift of recovery credits sent straight to your person's account — no physical voucher to lose or post.",
      },
      {
        question: "can I gift a private room session for a group?",
        answer:
          "yes — as well as credits, you can gift a private-room booking, which makes a great present for a birthday, a couple or a sports team.",
      },
      {
        question: "who is a recovery gift good for?",
        answer:
          "it suits the athlete, the stressed-out friend, the new parent, and the person who already has everything — anyone who'd appreciate time to feel good rather than another object.",
      },
      {
        question: "where is the gift redeemed?",
        answer:
          "at ZUZU, 5 edison way, century city, cape town — open seven days a week. the credits sit in the recipient's account until they're ready to book.",
      },
    ],
  },
  {
    path: "/corporate-gift-cards",
    title:
      "Corporate Gift Cards Cape Town | Best Corporate Gift Ideas — ZUZU Century City",
    description:
      "Looking for corporate gift ideas in Cape Town? ZUZU corporate gift cards send recovery credits for sauna, cold plunge, red light & InBody scans in Century City. Bulk orders, credits last three years.",
    keywords:
      "corporate gift cards Cape Town, best corporate gift ideas Cape Town, corporate gifting Cape Town, staff gift ideas Cape Town, client gifts Cape Town, employee wellness gifts Cape Town, year end staff gifts Cape Town, bulk gift vouchers Cape Town, corporate wellness Century City",
    faqs: [
      {
        question: "can I buy gift cards in bulk for my company?",
        answer:
          "yes — get in touch with how many people you're gifting and roughly what you'd like each card to be worth, and we'll set it up. it works the same for one card or a hundred.",
      },
      {
        question: "what makes recovery a good corporate gift?",
        answer:
          "one gift has to suit a whole team of different people. a recovery credit does that, because the recipient chooses what to spend it on — sauna, cold plunge, compression, red light or an inbody scan. there's no size to guess, nothing to store, and nothing to bin.",
      },
      {
        question: "do corporate gift cards expire?",
        answer:
          "no. the credits last three years, so a card given at year-end still gets used months later — nothing goes to waste and nobody feels rushed into booking.",
      },
      {
        question: "can we add our company's name or a message?",
        answer:
          "yes — tell us what you'd like the note to say when you order and we'll include it with each card.",
      },
      {
        question: "what can each recipient use their credits on?",
        answer:
          "anything in the circuit: the 90°c nordic sauna, cold plunges at 7° and 12°c, normatec compression boots, red-light therapy or an inbody body-composition scan. one credit is 30 minutes off-peak, 15 minutes peak — sunday is off-peak all day.",
      },
      {
        question: "is this suitable for client gifting as well as staff?",
        answer:
          "yes. it lands better than another bottle of wine, and it suits recipients who don't drink — which makes it a safer choice for a client list you don't know intimately.",
      },
      {
        question: "where do recipients redeem their gift?",
        answer:
          "at ZUZU, 5 edison way, century city, cape town — open seven days a week. the credits sit in each recipient's account until they book.",
      },
    ],
  },
  {
    path: "/group-day-out",
    title:
      "Group Activities Cape Town | Friends Day Out — ZUZU Recovery Century City",
    description:
      "Looking for something different to do with friends in Cape Town? Book the whole ZUZU recovery club in Century City — sauna, cold plunge & red light for up to 10. A fun, alcohol-free catch-up from R600.",
    keywords:
      "things to do with friends Cape Town, group activities Cape Town, fun things to do Cape Town, sober social Cape Town, group day out Cape Town, things to do in Century City, friends catch up Cape Town, alcohol free activities Cape Town",
    faqs: [
      {
        question: "what is there to do as a group?",
        answer:
          "your group gets the whole space: a 90°c nordic sauna, two cold plunges (7°c & 12°c), normatec compression boots, red-light therapy and an inbody scan. you move through the circuit together at your own pace, with your own music playing.",
      },
      {
        question: "how many friends can come?",
        answer:
          "up to 10 people per session. for a bigger crew we can arrange back-to-back slots so everyone gets the full circuit — just ask.",
      },
      {
        question: "do we need to be fit to do it?",
        answer:
          "not at all. everything is self-guided, so the cold plunge is as gentle or as brutal as each person makes it. all fitness levels are welcome.",
      },
      {
        question: "is it a good alcohol-free social activity?",
        answer:
          "yes — the buzz comes from the hot-cold contrast, not a bar. it's a genuinely different catch-up that sends everyone home feeling better rather than worse.",
      },
      {
        question: "how much does a group session cost?",
        answer:
          "R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak, sat/sun hours differ (sunday is off-peak all day), and split bookings are pro rata — for the whole space and up to 10 people. you can book from 30 minutes, from R600 off-peak.",
      },
      {
        question: "what's nearby afterwards?",
        answer:
          "we're in century city, cape town, with cafés and restaurants a short walk away, so most groups grab a coffee or a meal once they've finished the circuit.",
      },
    ],
  },
  {
    path: "/run-club-recovery",
    title:
      "Run Club Recovery Cape Town | Group Recovery for Runners — ZUZU Century City",
    description:
      "Bring your run club in for recovery after the long run. Private nordic sauna, cold plunge & Normatec compression boots for up to 10 runners in Century City, Cape Town. Flush the legs together from R600.",
    keywords:
      "run club recovery Cape Town, recovery for runners Cape Town, group recovery runners Cape Town, compression boots runners Cape Town, post run recovery Cape Town, runners cold plunge Cape Town, parkrun recovery Cape Town, running club Century City",
    faqs: [
      {
        question: "why bring a run club in for recovery?",
        answer:
          "running pounds the legs week after week. the compression boots flush tired legs, the sauna lifts circulation and the cold plunge helps with inflammation — and doing it together turns recovery into a club ritual rather than something everyone skips.",
      },
      {
        question: "what's best for heavy, tired legs?",
        answer:
          "the normatec compression boots are the runner's friend — sequential pulses that help move blood and lymph through fatigued legs. pair them with a sauna-to-plunge contrast round and most runners step out feeling lighter.",
      },
      {
        question: "how many runners can come at once?",
        answer:
          "up to 10 per session. bigger clubs can run back-to-back slots so the whole group gets the full circuit — just let us know your numbers.",
      },
      {
        question: "when after a run should we come in?",
        answer:
          "after the weekly club run or a long run is ideal — same day or the next. it's a flexible reset, so fit it to when your group already meets.",
      },
      {
        question: "how much does it cost for the club?",
        answer:
          "R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak, sat/sun hours differ (sunday is off-peak all day), and split bookings are pro rata — for the whole space and up to 10 runners. from 30 minutes, from R600 off-peak.",
      },
      {
        question: "can we set up a regular weekly recovery slot?",
        answer:
          "yes — get in touch and we'll help lock in a standing booking after your club run. off-peak slots are the best value for a weekly ritual (weekday off-peak is 9am–4pm; saturday and sunday hours differ, with sunday off-peak all day).",
      },
    ],
  },
  {
    path: "/marathon-recovery",
    title:
      "Marathon Recovery Cape Town | Post-Race & Training Recovery — ZUZU Century City",
    description:
      "Recover from marathon training and race day in Cape Town. Nordic sauna, cold plunge & Normatec compression boots in Century City to flush heavy legs and speed recovery. Book from R600.",
    keywords:
      "marathon recovery Cape Town, post marathon recovery Cape Town, two oceans recovery Cape Town, cape town marathon recovery, half marathon recovery Cape Town, compression boots marathon Cape Town, runner recovery Century City, post race recovery Cape Town",
    faqs: [
      {
        question: "how does recovery help after a marathon?",
        answer:
          "a marathon leaves the legs trashed and inflamed. contrast therapy lifts circulation and the compression boots help flush the heaviness, while red light supports muscle recovery. it doesn't replace rest, sleep and refuelling — it complements them so you bounce back sooner.",
      },
      {
        question:
          "I'm training for the cape town marathon or two oceans — can this help?",
        answer:
          "yes. runners building toward cape town's big races use recovery through the heavy-mileage blocks, not just after race day. a regular contrast-and-compression session after long runs helps you absorb the training.",
      },
      {
        question: "when should I come in after a race?",
        answer:
          "many runners do a gentle session in the days after a marathon, once the worst soreness settles. keep the cold plunge short and easy at first and listen to your body — this is recovery, not another workout.",
      },
      {
        question: "is it useful during training, not just after races?",
        answer:
          "very. the heaviest gains come from recovering well between long runs and speed sessions. a standing recovery slot through a training block keeps the legs fresher week to week.",
      },
      {
        question: "how much does a marathon recovery session cost?",
        answer:
          "R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak, sat/sun hours differ (sunday is off-peak all day), and split bookings are pro rata — up to 10 people. from 30 minutes, from R600 off-peak — enough for a compression-and-contrast reset.",
      },
      {
        question: "can I bring my running group?",
        answer:
          "yes — the private room takes up to 10, so a training squad can recover together after a long run. larger groups can book back-to-back slots.",
      },
    ],
  },
  {
    path: "/hyrox-recovery",
    title:
      "Hyrox Recovery Cape Town | Recovery for Hybrid Athletes — ZUZU Century City",
    description:
      "Recover from hyrox training and race day in Cape Town. Private nordic sauna, cold plunge & Normatec compression boots in Century City for the running-and-strength grind. Bring your crew, from R600.",
    keywords:
      "hyrox recovery Cape Town, hyrox training recovery Cape Town, hybrid athlete recovery Cape Town, hyrox cold plunge Cape Town, functional fitness recovery Cape Town, compression boots hyrox Cape Town, hyrox Cape Town recovery, hyrox recovery Century City",
    faqs: [
      {
        question: "why does hyrox training need serious recovery?",
        answer:
          "hyrox stacks eight 1km runs against eight functional stations — sled push and pull, burpee broad jumps, wall balls and more. that running-plus-strength load hammers the legs and the whole body, so contrast therapy and compression between sessions make a real difference.",
      },
      {
        question: "what helps most after a hyrox race or sim?",
        answer:
          "the legs take the brunt of the runs and the sled, so the compression boots are key. add a sauna-to-plunge contrast round to lift circulation and settle the full-body fatigue.",
      },
      {
        question: "can I bring my training partners or crew?",
        answer:
          "yes — the room takes up to 10, so a hyrox crew can recover together after a hard hybrid session. bigger groups can run back-to-back slots.",
      },
      {
        question: "when should we come in?",
        answer:
          "after a race, a sim, or a heavy hybrid session is ideal — and a lighter reset on a rest day works too. fit it around your training week.",
      },
      {
        question: "how much does a hyrox recovery session cost?",
        answer:
          "R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak, sat/sun hours differ (sunday is off-peak all day), and split bookings are pro rata — for the whole space and up to 10. from 30 minutes, from R600 off-peak.",
      },
      {
        question: "can we book a regular recovery slot for the crew?",
        answer:
          "yes — get in touch and we'll set up a standing booking so recovery becomes part of the training block. off-peak slots are the best value (weekday off-peak is 9am–4pm; saturday and sunday hours differ, with sunday off-peak all day).",
      },
    ],
  },
  {
    path: "/crossfit-recovery",
    title:
      "CrossFit Recovery Cape Town | Recovery for Boxes & Athletes — ZUZU Century City",
    description:
      "Recover from CrossFit and functional training in Cape Town. Private nordic sauna, cold plunge & Normatec compression boots in Century City for the whole box, up to 10. Book from R600.",
    keywords:
      "crossfit recovery Cape Town, functional fitness recovery Cape Town, crossfit box recovery Cape Town, wod recovery Cape Town, crossfit cold plunge Cape Town, compression boots crossfit Cape Town, crossfit athlete recovery Century City, crossfit community Cape Town",
    faqs: [
      {
        question: "why recover after crossfit?",
        answer:
          "high-intensity metcons and heavy lifting leave you sore from head to toe. the sauna-to-plunge contrast helps circulation and inflammation, and the compression boots flush the legs — so you front up to the next wod feeling fresher.",
      },
      {
        question: "can the whole box come together?",
        answer:
          "yes — the private room takes up to 10, so a class or crew can recover together after a tough session. larger boxes can book back-to-back slots so everyone gets the circuit.",
      },
      {
        question: "what's best after a brutal wod or benchmark?",
        answer:
          "a sauna round into the cold plunge for the full-body soreness, plus the compression boots for trashed legs after all those squats and runs. red light is a gentle finisher for muscle recovery.",
      },
      {
        question: "when should we come in?",
        answer:
          "after a benchmark, a heavy day, or a competition is ideal, and a weekly reset on a rest day works well too. fit it around your box's schedule.",
      },
      {
        question: "how much does it cost for the box?",
        answer:
          "R1,000 per hour off-peak and R2,000 per hour peak — weekdays that's 9am–4pm off-peak, sat/sun hours differ (sunday is off-peak all day), and split bookings are pro rata — for the whole space and up to 10. from 30 minutes, from R600 off-peak.",
      },
      {
        question: "can we set up a regular recovery slot?",
        answer:
          "yes — get in touch and we'll arrange a standing booking for your box. off-peak slots are the best value for a weekly recovery ritual (weekday off-peak is 9am–4pm; saturday and sunday hours differ, with sunday off-peak all day).",
      },
    ],
  },
];

export const routeByPath: Record<string, RouteSeo> = Object.fromEntries(
  routes.map((r) => [r.path, r]),
);

/** Build a schema.org FAQPage object from a list of FAQ items. */
export function faqJsonLd(faqs: FaqItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}
